/**
 * Admin "OG copy" panel — per-route Open-Graph / Twitter social-copy overrides.
 *
 * Each marketing route shows its live default (from SeoHead PAGE_META) and an
 * editable override. Saving an override changes only the social-share title /
 * description (og:* + twitter:*) — the on-page SEO title stays as-is. Clearing
 * both fields reverts the route to its default. No redeploy needed; the public
 * SeoHead picks up changes within 5 minutes (cache) or on next load.
 */
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Save, RotateCcw, CheckCircle2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const RouteRow = ({ row, limits, onSaved }) => {
  const [title, setTitle] = useState(row.og_title || "");
  const [desc, setDesc] = useState(row.og_description || "");
  const [saving, setSaving] = useState(false);
  const dirty = (title || "") !== (row.og_title || "") || (desc || "") !== (row.og_description || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/og-copy", { og_title: title || null, og_description: desc || null }, { params: { route: row.route } });
      toast.success(`Saved social copy for ${row.route}`);
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete("/admin/og-copy", { params: { route: row.route } });
      setTitle("");
      setDesc("");
      toast.success(`Reverted ${row.route} to default copy`);
      onSaved();
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={`og-copy-row-${row.route}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[13px] font-semibold text-black">{row.label}</div>
          <code className="text-[11px] text-zinc-500">{row.route}</code>
        </div>
        {row.has_override ? (
          <span className="inline-flex items-center gap-1 text-[#7737F6] text-[10.5px] font-semibold tracking-[0.14em] uppercase" data-testid={`og-copy-overridden-${row.route}`}>
            <CheckCircle2 size={12} /> Custom
          </span>
        ) : (
          <span className="text-zinc-400 text-[10.5px] font-semibold tracking-[0.14em] uppercase">Default</span>
        )}
      </div>

      <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500 mb-1">Share title</label>
      <input
        type="text"
        value={title}
        maxLength={limits.title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={row.default_title}
        className="w-full border border-zinc-200 px-3 py-2 text-[13px] focus:border-[#7737F6] focus:outline-none mb-1"
        data-testid={`og-copy-title-input-${row.route}`}
      />
      <div className="text-[11px] text-zinc-400 mb-3">{title.length}/{limits.title} · default: <span className="text-zinc-500">{row.default_title}</span></div>

      <label className="block text-[11px] tracking-[0.14em] uppercase text-zinc-500 mb-1">Share description</label>
      <textarea
        rows={2}
        value={desc}
        maxLength={limits.description}
        onChange={(e) => setDesc(e.target.value)}
        placeholder={row.default_description}
        className="w-full border border-zinc-200 px-3 py-2 text-[13px] focus:border-[#7737F6] focus:outline-none mb-1 resize-y"
        data-testid={`og-copy-desc-input-${row.route}`}
      />
      <div className="text-[11px] text-zinc-400 mb-3">{desc.length}/{limits.description}</div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="ek-btn ek-btn-primary text-[12.5px] flex items-center gap-1 disabled:opacity-40"
          data-testid={`og-copy-save-${row.route}`}
        >
          <Save size={13} /> {saving ? "Saving…" : "Save"}
        </button>
        {row.has_override ? (
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            className="text-zinc-600 hover:text-black text-[12px] flex items-center gap-1 border border-zinc-200 px-3 py-1.5 disabled:opacity-50"
            data-testid={`og-copy-reset-${row.route}`}
          >
            <RotateCcw size={12} /> Revert to default
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default function OgCopyPanel() {
  const [rows, setRows] = useState([]);
  const [limits, setLimits] = useState({ title: 120, description: 320 });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/og-copy");
      setRows(data?.routes || []);
      if (data?.limits) setLimits(data.limits);
    } catch {
      toast.error("Failed to load OG copy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const overrideCount = rows.filter((r) => r.has_override).length;

  return (
    <div data-testid="admin-og-copy-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Share2 size={18} className="text-[#7737F6]" /> Social share copy</h2>
          <p className="text-zinc-500 text-[13px] mt-1 max-w-2xl">
            Override the Open-Graph / Twitter share title &amp; description per route — the text people see when a page is shared on LinkedIn, Slack or WhatsApp. Leave blank to use the on-page default. {overrideCount > 0 ? <span className="text-[#7737F6] font-medium">{overrideCount} custom override{overrideCount === 1 ? "" : "s"} live.</span> : null}
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="text-zinc-500 hover:text-black text-[12px] flex items-center gap-1 disabled:opacity-50"
          data-testid="og-copy-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-400 text-[13px] py-8">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => (
            <RouteRow key={r.route} row={r} limits={limits} onSaved={reload} />
          ))}
        </div>
      )}
    </div>
  );
}
